// Storage policy + browser-quota checks.
//
// Two ledgers:
//  - drawolaUsage (from usage.ts): our bytes → policy checks
//  - navigator.storage.estimate().usage: origin-wide → quota checks
//
// All UI percentages use effectiveLimit = min(policyTotal, quota * headroom).

import { LIMITS } from "./types";

import { getDrawolaUsage } from "./usage";
import { STORES, promisify, withTx } from "./db";

import type { CanWriteResult, StorageSnapshot } from "./types";

export async function browserEstimate(): Promise<{
  quota: number;
  usage: number;
}> {
  if (typeof navigator !== "undefined" && navigator.storage?.estimate) {
    try {
      const { quota = 0, usage = 0 } = await navigator.storage.estimate();
      return { quota, usage };
    } catch {
      return { quota: 0, usage: 0 };
    }
  }
  return { quota: 0, usage: 0 };
}

export async function effectiveLimit(): Promise<number> {
  const { quota } = await browserEstimate();
  const quotaLimit = quota * LIMITS.QUOTA_HEADROOM_RATIO;
  return Math.min(LIMITS.POLICY_TOTAL_BYTES, quotaLimit || 0);
}

export async function isPersisted(): Promise<boolean> {
  try {
    return (await navigator.storage?.persisted?.()) ?? false;
  } catch {
    return false;
  }
}

export async function requestPersist(): Promise<boolean> {
  try {
    if (!navigator.storage?.persist) {
      return false;
    }
    if (await navigator.storage.persisted()) {
      return true;
    }
    return await navigator.storage.persist();
  } catch {
    return false;
  }
}

export async function isLowQuota(): Promise<boolean> {
  const { quota } = await browserEstimate();
  return quota > 0 && quota < LIMITS.LOW_QUOTA_THRESHOLD;
}

async function countDocs(): Promise<number> {
  return withTx([STORES.DOCS], "readonly", async (tx) => {
    return promisify(tx.objectStore(STORES.DOCS).count());
  });
}

async function getDocSizeBytes(docId: string): Promise<number> {
  return withTx([STORES.DOCS], "readonly", async (tx) => {
    const v = await promisify(
      tx.objectStore(STORES.DOCS).get(docId) as IDBRequest<
        { sizeBytes?: number } | undefined
      >,
    );
    return v?.sizeBytes ?? 0;
  });
}

/**
 * Pre-flight check before every write path.
 *
 * @param nextTotalBytes new doc's size after the write (not delta)
 * @param docId          existing doc id (undefined when creating a new doc)
 */
export async function canWrite(
  nextTotalBytes: number,
  docId?: string,
): Promise<CanWriteResult> {
  // Policy: max docs
  if (!docId) {
    const n = await countDocs();
    if (n >= LIMITS.MAX_DOCUMENTS) {
      return {
        ok: false,
        reason: "doc-count",
        meta: { max: LIMITS.MAX_DOCUMENTS },
      };
    }
  }
  // Policy: per-doc size
  if (nextTotalBytes > LIMITS.MAX_BYTES_PER_DOC) {
    return {
      ok: false,
      reason: "doc-size",
      meta: { size: nextTotalBytes },
    };
  }
  // Policy: total
  const drawolaUsage = await getDrawolaUsage();
  const existingSize = docId ? await getDocSizeBytes(docId) : 0;
  const projected = drawolaUsage - existingSize + nextTotalBytes;
  if (projected > LIMITS.POLICY_TOTAL_BYTES) {
    return {
      ok: false,
      reason: "policy-total",
      meta: {},
    };
  }
  // Browser quota
  const { quota, usage } = await browserEstimate();
  const quotaLimit = quota * LIMITS.QUOTA_HEADROOM_RATIO;
  if (quotaLimit > 0 && usage + (nextTotalBytes - existingSize) > quotaLimit) {
    return {
      ok: false,
      reason: "quota",
      meta: { quota, usage },
    };
  }
  return { ok: true };
}

export async function getSnapshot(): Promise<StorageSnapshot> {
  // Best-effort persist retry on every snapshot read. `requestPersist`
  // short-circuits when already granted (no extra browser work), and when
  // denied it re-asks — letting increased user engagement (bookmarks, PWA
  // install, etc.) promote us to persistent without requiring a reload.
  const [{ quota, usage: originUsage }, drawolaUsage, persisted, docCount] =
    await Promise.all([
      browserEstimate(),
      getDrawolaUsage(),
      requestPersist(),
      countDocs(),
    ]);
  const quotaLimit = quota * LIMITS.QUOTA_HEADROOM_RATIO;
  const effective = Math.min(LIMITS.POLICY_TOTAL_BYTES, quotaLimit || 0);
  return {
    drawolaUsage,
    originUsage,
    quota,
    effectiveLimit: effective,
    docCount,
    persisted,
  };
}

export function formatMB(bytes: number): string {
  if (bytes >= 1024 * 1024 * 1024) {
    return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
  }
  if (bytes >= 1024 * 1024) {
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }
  if (bytes >= 1024) {
    return `${(bytes / 1024).toFixed(1)} KB`;
  }
  return `${bytes} B`;
}
